import { useTranslation } from 'react-i18next';
import { useBottomAligned } from '../useBottomAligned';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import {
  ClickableRow,
  LOADING_SKELETON_HEIGHT,
  LoadingSkeleton,
  ScrollArea,
  StickyHeadCell,
  TableEmpty,
  TableRoot
} from './AnalyticsGroupsTableStyles';
import { formatCellValue, getColumnAlign, GROUP_COLUMN_KEY } from '../analyticsColumns';
import { formatGroupLabel } from '../analyticsDateUtils';

interface Props {
  groups: AnalyticsGroupDto[];
  columns: ColumnMetaDto[];
  groupBy: string;
  groupColumnLabelKey?: string;
  onGroupClick?: (group: AnalyticsGroupDto) => void;
  loading?: boolean;
  empty?: boolean;
}

export const AnalyticsGroupsTable = ({ groups, columns, groupBy, groupColumnLabelKey, onGroupClick, loading, empty }: Props) => {
  const { t, i18n } = useTranslation();

  const scrollRef = useBottomAligned();

  if (loading) {
    return (
      <TableRoot>
        <LoadingSkeleton variant="rectangular" height={LOADING_SKELETON_HEIGHT} />
      </TableRoot>
    );
  }

  if (empty || groups.length === 0) {
    return (
      <TableRoot>
        <TableEmpty>{t('analytics.table.empty')}</TableEmpty>
      </TableRoot>
    );
  }

  const clickable = !!onGroupClick;

  return (
    <TableRoot>
      <ScrollArea ref={scrollRef}>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <StickyHeadCell key={col.key} align={getColumnAlign(col.type)}>
                  {col.key === GROUP_COLUMN_KEY ? t(groupColumnLabelKey ?? 'analytics.table.groupColumn') : t(col.labelKey)}
                </StickyHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g, idx) => {
              const groupLabel = formatGroupLabel(g.groupKey, groupBy, t('analytics.table.unknownGroup'), i18n);
              const rowKey = g.groupKey.id || `row-${idx}`;
              return (
                <ClickableRow key={rowKey} clickable={clickable} onClick={onGroupClick ? () => onGroupClick(g) : undefined}>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={getColumnAlign(col.type)}>
                      {col.key === GROUP_COLUMN_KEY ? groupLabel : formatCellValue(g.values?.[col.key], col.type, i18n)}
                    </TableCell>
                  ))}
                </ClickableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </TableRoot>
  );
};
